import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PersistentWorkItemState } from './persistent-work-item-state';
import { PersistentWorkflowSnapshot } from './persistent-workflow-snapshot';
import { PersistentWorkflowStore } from './persistent-workflow-store';

/**
 * Implementation of PersistentWorkflowStore by local storage.
 */
export class PersistentLocalStorage extends PersistentWorkflowStore {
    private nextInstanceId = 1;

    /**
     * Save the work item snapshot to the local storage.
     *
     * @param version the version number.
     * @param workItemId the id of work item.
     * @param workItemState the state of work item.
     * @param workItemData the persistent data of work item.
     * @param instanceId the instance ID if it has already.
     */
    public save(
        version: number,
        workItemId: number,
        workItemState: PersistentWorkItemState,
        workItemData: any,
        instanceId?: number): Observable<PersistentWorkflowSnapshot> {

        return of(null)
            .pipe(map(() => {
                const indexData = this.readIndexData();
                if (MsftSme.isNullOrUndefined(instanceId)) {
                    instanceId = this.nextInstanceId++;
                    indexData.instances.push(instanceId);
                    this.writeIndexData(indexData);
                }

                const snapshot: PersistentWorkflowSnapshot = { version, instanceId, workItemId, workItemState, workItemData };
                this.writeInstance(snapshot);
                return snapshot;
            }));
    }

    /**
     * Restore the snapshot data for the workflow.
     */
    public restore(): Observable<PersistentWorkflowSnapshot[]> {
        return of(null)
            .pipe(map(() => {
                const instances: PersistentWorkflowSnapshot[] = [];
                const indexData = this.readIndexData();
                if (indexData.instances.length === 0) {
                    return instances;
                }

                for (const instanceId of indexData.instances) {
                    const instance = this.readInstance(instanceId);
                    if (instance) {
                        instances.push(instance);
                        if (this.nextInstanceId < instanceId) {
                            this.nextInstanceId = instanceId;
                        }
                    } else {
                        this.clear(instanceId);
                    }
                }

                return instances;
            }));
    }

    /**
     * Clear the snapshot for the instance id.
     * If not specified, clear all snapshots.
     *
     * @param instanceId the instance id.
     */
    public clear(instanceId?: number): Observable<void> {
        return of(null)
            .pipe(tap(() => {
                const indexData = this.readIndexData();

                // remove one.
                if (!MsftSme.isNullOrUndefined(instanceId)) {
                    const pointer = indexData.instances.indexOf(instanceId);
                    if (pointer >= 0) {
                        indexData.instances.splice(pointer, 1);
                        this.writeIndexData(indexData);
                    }

                    MsftSme.LocalStorageHandler.removeItem(this.instanceKey(instanceId));

                    return;
                }

                // remove all.
                MsftSme.LocalStorageHandler.removeItem(this.indexKey());
                for (const index2 of indexData.instances) {
                    MsftSme.LocalStorageHandler.removeItem(this.instanceKey(index2));
                }

                return;
            }));
    }

    /**
     * Gets the key of index data.
     */
    private indexKey(): string {
        return `${this.moduleName}@${this.name}:index`;
    }

    /**
     * Gets the key of snapshot instance.
     *
     * @param instanceId the instance Id.
     */
    private instanceKey(instanceId: number): string {
        return `${this.moduleName}@${this.name}:${instanceId}`;
    }

    /**
     * Reads the index data to enumerate all snapshots.
     */
    private readIndexData(): { instances: number[] } {
        let indexData: { instances: number[] } = null;
        const item = MsftSme.LocalStorageHandler.getItem(this.indexKey());
        if (item) {
            indexData = JSON.parse(item);
        } else {
            indexData = { instances: [] };
        }

        return indexData;
    }

    /**
     * Writes the index data.
     *
     * @param indexData the index data.
     */
    private writeIndexData(indexData: { instances: number[] }): void {
        MsftSme.LocalStorageHandler.setItem(this.indexKey(), JSON.stringify(indexData));
    }

    /**
     * Reads the snapshot of the instance.
     *
     * @param instanceId the instance ID.
     */
    private readInstance(instanceId: number): PersistentWorkflowSnapshot {
        return JSON.parse(MsftSme.LocalStorageHandler.getItem(this.instanceKey(instanceId)) ?? '{}');
    }

    /**
     * Writes the snapshot.
     *
     * @param snapshot the snapshot.
     */
    private writeInstance(snapshot: PersistentWorkflowSnapshot): void {
        MsftSme.LocalStorageHandler.setItem(this.instanceKey(snapshot.instanceId), JSON.stringify(snapshot));
    }
}
